import { CFDFile, DeploySet, SelectedSystem, SystemCode } from '@aero-platform/shared'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { useGetDeploySets } from '../api/deploy-set-API'
import {
  useCreateMethodologySet,
  useGetMethodologySets,
  useUpdateMethodologySet
} from '../api/methodology-set-API'
import { ReactComponent as LoadingIcon } from '../assets/svg/loading.svg'
import SaveButton from '../components/buttons/SaveButton'
import HardwareSettingsSection from '../components/HardwareSettingsSection'
import { CopyIcon, PlusIcon } from '../components/Icons'
import JobRunParamsSection, { JobRunParamEntry } from '../components/JobRunParamsSection'
import MethodSetSection from '../components/MethodSetSection'
import StarCCMRunConfigForm from '../components/submission/StarCCMRunConfigForm'
import Upload from '../components/upload/Upload'
import { protoService } from '../proto/ProtoService'
import {
  BasicNameValue,
  MethodologySet,
  MiscellaneousParameterList,
  StarCCMConfig,
  StarCCMParameterList
} from '../types/types'
import {
  cloneDeploySet,
  cloneSelectedSystem,
  cloneStarCCMConfig,
  cloneStarCCMDir,
  cloneSystemCodeFiles,
  toStringMap
} from '../types/util'
import {
  starCCMMacroSteps,
  UploadTypes,
  MethodologyStatusList,
  METHODOLOGY_STATUS_DRAFT
} from '../utils/constants'
import { appEventInstance } from '../utils/event'
import { currentTeamState, currentUserState } from '../utils/state-atoms'
import { CheckboxStyle, DropdownStyle } from '../utils/styling-constants'

type MethodForm = {
  name: string
  description: string
  status?: string
  selectedSystems: SelectedSystem[]
}

const MethodologySets: FC = () => {
  const [scrollY, setScrollY] = useState(0)
  const [mode, setMode] = useState('')
  const [env, setEnv] = useState('prod')
  const [methodologySets, setMethodologySets] = useState<MethodologySet[]>([])
  const [currentMethodologySet, setCurrentMethodologySet] = useState<MethodologySet>()

  const [{ loading: loadingMethodologySets }, fetchMethodologySets] = useGetMethodologySets()
  const [{ loading: creating }, createMethodologySet] = useCreateMethodologySet()
  const [{ loading: updating }, updateMethodologySet] = useUpdateMethodologySet()

  const currentUser = useRecoilValue(currentUserState)
  const currentTeam = useRecoilValue(currentTeamState)

  const [, fetchDeploySets] = useGetDeploySets()

  // File upload state for StarCCM methodology files
  type FileData = {
    name: string
    path: string
    rawFile?: File
    progress?: number
    uploading?: boolean
    failed?: boolean
    abortController?: AbortController
  }
  const [selectedSimTemplateFiles, setSelectedSimTemplateFiles] = useState<FileData[]>([])
  const [selectedCodeFiles, setSelectedCodeFiles] = useState<FileData[]>([])
  const [aeroTempRunId, setAeroTempRunId] = useState<string | null>(null)

  // Check if any files are still uploading
  const hasUploadingFiles =
    selectedSimTemplateFiles.some(f => f.uploading) || selectedCodeFiles.some(f => f.uploading)

  const sections = ['ansa', 'starccmp', 'ensight']
  const [sectionVisibilities, setSectionVisibilities] = useState<Map<string, boolean>>(
    new Map([
      ['ansa', false],
      ['starccmp', false],
      ['ensight', false]
    ])
  )
  const [sectionIndexes, setSectionIndexes] = useState<Map<string, number>>(
    new Map([
      ['ansa', -1],
      ['starccmp', -1],
      ['ensight', -1]
    ])
  )
  const [sectionNames] = useState<Map<string, string>>(
    new Map([
      ['ansa', 'ANSA'],
      ['starccmp', 'StarCCM'],
      ['ensight', 'Ensight']
    ])
  )
  const [sectionDeploySets, setSectionDeploySets] = useState<Map<string, DeploySet[]>>(new Map())
  const [selectedSystems, setSelectedSystems] = useState<SelectedSystem[]>([])
  const [jobRunParams, setJobRunParams] = useState<JobRunParamEntry[]>([])

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<MethodForm>()

  const selectedStarCCMPreset: StarCCMParameterList =
    currentMethodologySet && currentMethodologySet.starccmConfig?.starccmParameters
  const selectedMiscPreset: MiscellaneousParameterList =
    currentMethodologySet && currentMethodologySet.starccmConfig?.miscellaneousParameters
  const selectedMacroSteps =
    (currentMethodologySet && currentMethodologySet.starccmConfig?.macroSteps) || []

  // Compute customMacros from both saved systemCodeFiles and pending uploads
  const customMacros: string[] = useMemo(() => {
    const macroSet = new Set<string>()

    // Add macros from saved systemCodeFiles (for existing methodologies)
    if (currentMethodologySet?.systemCodeFiles) {
      currentMethodologySet.systemCodeFiles
        .filter(file => file.fileRef?.fileType === 'code')
        .forEach(file => {
          if (file.fileRef?.file && file.fileRef.file.endsWith('.java')) {
            macroSet.add(file.fileRef.file)
          }
        })
    }

    // Add macros from pending uploads (for new methodologies or additional uploads)
    selectedCodeFiles
      .filter(f => !f.uploading && !f.failed && f.name.endsWith('.java'))
      .forEach(file => {
        macroSet.add(file.name)
      })

    return Array.from(macroSet).sort()
  }, [currentMethodologySet?.systemCodeFiles, selectedCodeFiles])

  const refStarCCMConfig = useRef<StarCCMConfig | undefined>(undefined)

  console.log(selectedStarCCMPreset, selectedMiscPreset)

  useEffect(() => {
    fetchMethodologySets()
      .then(res => {
        const { data } = res
        if (data) {
          const tmpList = data.protoBytesList.map((item: string) => {
            return protoService.deSerialize(item, 'methodologyset')
          })
          tmpList && setMethodologySets(tmpList)
        }
      })
      .catch(err => {
        setMethodologySets([])
        toast.error('Error loading methodology sets.')
      })

    fetchDeploySets()
      .then(res => {
        const { data } = res
        if (data) {
          const tmpList = data.protoBytesList.map((item: string) => {
            return protoService.deSerialize(item, 'deployset')
          })

          const newDsMap = new Map<string, DeploySet[]>(sectionDeploySets)
          sections.forEach(section => {
            const dsBySection = tmpList.filter((item: DeploySet) => item.system === section)
            newDsMap.set(section, dsBySection)
          })
          console.log(newDsMap)
          setSectionDeploySets(newDsMap)
        }
      })
      .catch(err => {
        toast.error('Error loading deploy sets.')
      })

    const handleScroll = () => {
      setScrollY(window.scrollY)
    }

    window.addEventListener('scroll', handleScroll)

    // Clean up on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam])

  const onValidationError: SubmitErrorHandler<MethodForm> = errors => {
    console.error('Validation errors:', errors)
    toast.error('Please fix the validation errors before submitting the form.')
  }

  const onSubmit: SubmitHandler<MethodForm> = async (formData: MethodForm) => {
    if (creating || updating) {
      return
    }
    console.log(formData)
    const newMethodSet = new MethodologySet()
    if (mode === 'edit') {
      newMethodSet._id = currentMethodologySet?._id as string
      newMethodSet.name = formData.name
    }

    newMethodSet.description = formData.description
    newMethodSet.status = formData.status || METHODOLOGY_STATUS_DRAFT
    newMethodSet.selectedSystems = []
    sections.forEach(sectionKey => {
      if (!sectionVisibilities.get(sectionKey)) {
        //skip if not not available/unchecked
        return
      }
      const selectedSystem = formData.selectedSystems[sectionIndexes.get(sectionKey)]
      selectedSystem.name = sectionKey
      const selectedDeploySet = sectionDeploySets.get(sectionKey).find(deploySet => {
        return (
          deploySet.vendorCompatibilityVersion === selectedSystem.vendorVersion &&
          deploySet.version === selectedSystem.branchName &&
          deploySet.commitId === selectedSystem.commitId
        )
      })
      if (selectedDeploySet) {
        selectedSystem.deploySet = cloneDeploySet(selectedDeploySet)
      }
      newMethodSet.selectedSystems.push(cloneSelectedSystem(selectedSystem))
    })
    if (refStarCCMConfig.current) {
      newMethodSet.starccmConfig = cloneStarCCMConfig(refStarCCMConfig.current)
    }

    // When copying, preserve template files from the source methodology set
    // UNLESS we have new uploads (indicated by aeroTempRunId), in which case use the uploaded files.
    // Code files always come from selectedCodeFiles (the current UI state).
    if (mode === 'copy' && currentMethodologySet && !aeroTempRunId) {
      const uploadedSystemCodeFiles: SystemCode[] = []
      const seenFiles = new Set<string>()

      // Get template files: prefer selectedSimTemplateFiles (respects user deletions)
      const currentTemplateNames = new Set(selectedSimTemplateFiles.map(f => f.name))
      if (currentMethodologySet.systemCodeFiles) {
        currentMethodologySet.systemCodeFiles
          .filter(systemCodeFile => {
            const fileName = systemCodeFile.fileRef?.file || systemCodeFile.fileName || ''
            if (seenFiles.has(fileName)) return false
            if (
              systemCodeFile.fileRef?.fileType === 'template' &&
              currentTemplateNames.has(fileName)
            ) {
              seenFiles.add(fileName)
              return true
            }
            return false
          })
          .forEach(systemCodeFile =>
            uploadedSystemCodeFiles.push(cloneSystemCodeFiles(systemCodeFile))
          )
      }

      // Get code files from selectedCodeFiles (includes files from systemCodeFiles + customSteps fallback)
      const customStepsArray = newMethodSet.starccmConfig?.customSteps || []
      selectedCodeFiles
        .filter(f => !f.uploading && !f.failed)
        .filter(file => file.name.endsWith('.java'))
        .filter(file => customStepsArray.includes(file.name))
        .forEach(file => {
          if (!seenFiles.has(file.name)) {
            seenFiles.add(file.name)
            const systemCode = new SystemCode()
            systemCode.name = file.name
            systemCode.fileName = file.name
            systemCode.systemType = 'starccm'
            const codeBlobPath =
              file.path && file.path !== file.name
                ? file.path
                : `${currentMethodologySet.name}/starccm/code/${file.name}`
            systemCode.fileRef = new CFDFile({
              file: file.name,
              path: codeBlobPath,
              fileType: 'code',
              system: 'starccm',
              raceTeamId: currentTeam,
              rootType: 'methodologies',
              storageAcct: process.env.REACT_APP_STORAGE_ACCOUNT || '',
              containerName: process.env.REACT_APP_CONTAINER_NAME || 'cfd-data',
              version: currentMethodologySet.name
            })
            uploadedSystemCodeFiles.push(systemCode)
          }
        })

      newMethodSet.systemCodeFiles = uploadedSystemCodeFiles

      // Preserve starccm object which includes the template file
      if (currentMethodologySet.starccm) {
        newMethodSet.starccm = cloneStarCCMDir(currentMethodologySet.starccm)
      }
    } else {
      // Build systemCodeFiles from uploaded files
      const uploadedSystemCodeFiles: SystemCode[] = []
      const fileNames = new Set<string>() // Track files to prevent duplicates

      // Add sim template files (deduplicated)
      const methodName = newMethodSet.name || 'new'
      selectedSimTemplateFiles
        .filter(f => !f.uploading && !f.failed)
        .forEach(file => {
          if (!fileNames.has(file.name)) {
            fileNames.add(file.name)
            const systemCode = new SystemCode()
            systemCode.name = file.name
            systemCode.fileName = file.name
            systemCode.systemType = 'starccm'
            const templateBlobPath =
              file.path && file.path !== file.name
                ? file.path
                : `${methodName}/starccm/template/${file.name}`
            systemCode.fileRef = new CFDFile({
              file: file.name,
              path: templateBlobPath,
              fileType: 'template',
              system: 'starccm',
              raceTeamId: currentTeam,
              rootType: 'methodologies',
              storageAcct: process.env.REACT_APP_STORAGE_ACCOUNT || '',
              containerName: process.env.REACT_APP_CONTAINER_NAME || 'cfd-data',
              version: methodName
            })
            uploadedSystemCodeFiles.push(systemCode)
          }
        })

      // Add code files - only include files that are referenced in customSteps (deduplicated)
      const customStepsArray = newMethodSet.starccmConfig?.customSteps || []
      selectedCodeFiles
        .filter(f => !f.uploading && !f.failed)
        .filter(file => file.name.endsWith('.java')) // Only include .java files
        .filter(file => customStepsArray.includes(file.name)) // Only include files used in custom steps
        .forEach(file => {
          if (!fileNames.has(file.name)) {
            fileNames.add(file.name)
            const systemCode = new SystemCode()
            systemCode.name = file.name
            systemCode.fileName = file.name
            systemCode.systemType = 'starccm'
            const codeBlobPath =
              file.path && file.path !== file.name
                ? file.path
                : `${methodName}/starccm/code/${file.name}`
            systemCode.fileRef = new CFDFile({
              file: file.name,
              path: codeBlobPath,
              fileType: 'code',
              system: 'starccm',
              raceTeamId: currentTeam,
              rootType: 'methodologies',
              storageAcct: process.env.REACT_APP_STORAGE_ACCOUNT || '',
              containerName: process.env.REACT_APP_CONTAINER_NAME || 'cfd-data',
              version: methodName
            })
            uploadedSystemCodeFiles.push(systemCode)
          }
        })

      newMethodSet.systemCodeFiles = uploadedSystemCodeFiles
    }

    newMethodSet.isHardwareOverride = false
    // Hardware overrides are now stored per selected system.
    // Also write first active override to the top-level fields for backward compatibility
    // with downstream consumers (Java backend) that read methodologySet.hardwareOverrides.
    newMethodSet.selectedSystems.forEach(sys => {
      if (sys.isHardwareOverride && sys.hardwareOverrides) {
        newMethodSet.isHardwareOverride = true
        if (!newMethodSet.hardwareOverrides) {
          newMethodSet.hardwareOverrides = sys.hardwareOverrides
        }
      }
    })

    newMethodSet.jobRunParams = new Map(
      jobRunParams
        .map(entry => [entry.key.trim(), entry.value] as [string, string])
        .filter(([key]) => key.length > 0)
    )

    // Validate that each system's total cores (coresPerSlot) does not exceed its Max Cores Count.
    for (const sys of newMethodSet.selectedSystems) {
      if (!sys.isHardwareOverride || !sys.hardwareOverrides) {
        continue
      }
      const coresPerSlot = sys.hardwareOverrides.coresPerSlot || 0
      const maxCoresCount = sys.maxCoresCount || 0
      if (coresPerSlot > maxCoresCount) {
        toast.error(
          `${sectionNames.get(sys.name) || sys.name}: Total Cores (${coresPerSlot}) cannot exceed Max Cores Count (${maxCoresCount}).`
        )
        return
      }
    }

    // set deployset id
    const dataToPost = protoService.encodePayload(newMethodSet, 'methodologyset')

    if (mode === 'new' || mode === 'copy') {
      // Add query params for copy operation or new uploads
      const params: any = { data: dataToPost }
      if (mode === 'copy' && currentMethodologySet) {
        params.params = {
          sourceMethodologyId: currentMethodologySet._id,
          sourceMethodologyName: currentMethodologySet.name
        }
      } else if (mode === 'new' && aeroTempRunId) {
        // Pass the temp run ID so server can move staged files
        params.params = {
          aeroTempRunId: aeroTempRunId
        }
      }

      createMethodologySet(params)
        .then(res => {
          // Check for backend error or partial failure
          const hasProto = res.data && res.data.protoBytesList && res.data.protoBytesList.length > 0
          const hasErrors = res.data && Array.isArray(res.data.errors) && res.data.errors.length > 0
          if (hasProto) {
            const tempMethodSet = protoService.deSerialize(
              res.data.protoBytesList[0],
              'methodologyset'
            )
            setMethodologySets([...methodologySets, tempMethodSet])
            setCurrentMethodologySet(tempMethodSet)
            handleItemSelected(tempMethodSet)
            setMode('edit')
            // Clear the temp run ID after successful creation
            setAeroTempRunId(null)
            localStorage.removeItem('aero_temp_run_id')
            if (hasErrors) {
              toast.error(
                <span>
                  Methodology set duplicated, but some files failed to copy:
                  <br />
                  <ul>
                    {res.data.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </span>
              )
            } else if (tempMethodSet.starccm && tempMethodSet.starccm.codeDir) {
              if (
                mode === 'copy' &&
                (currentMethodologySet?.systemCodeFiles?.length > 0 ||
                  currentMethodologySet?.starccm?.template)
              ) {
                // Duplicating with files
                toast.success(
                  <span>
                    Methodology set duplicated successfully! Files copied to:{' '}
                    <i>{tempMethodSet.starccm.codeDir}</i>
                  </span>
                )
              } else {
                // New methodology set
                toast.success(
                  <span>
                    Methodology set saved and new folder created. Please place your files here:{' '}
                    <i>{tempMethodSet.starccm.codeDir}</i>
                  </span>
                )
              }
            } else {
              toast.success('Methodology set saved but failed to create folder in blob storage.')
            }
            appEventInstance.reloadMethodology()
          } else if (hasErrors) {
            toast.error(
              <span>
                Error creating methodology set:
                <br />
                <ul>
                  {res.data.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </span>
            )
          }
        })
        .catch(err => {
          console.error(err)
          toast.error('Error creating methodology set.')
        })
    } else if (mode === 'edit') {
      try {
        // Build query params for update
        const params: any = {
          id: currentMethodologySet?._id as string,
          data: dataToPost
        }

        // If we have uploaded files, pass the temp run ID so server can move staged files
        if (aeroTempRunId) {
          params.params = {
            aeroTempRunId: aeroTempRunId
          }
        }

        const res = await updateMethodologySet(params)

        if (!res || !res.data) {
          return
        }

        if (res.data && res.data.protoBytesList && res.data.protoBytesList.length > 0) {
          protoService.deSerialize(res.data.protoBytesList[0], 'methodologyset')
          const updatedMethods = methodologySets.map(item => {
            if (item._id === currentMethodologySet?._id) {
              item.description = newMethodSet.description
              item.selectedSystems = newMethodSet.selectedSystems
              item.starccmConfig = newMethodSet.starccmConfig
              item.systemCodeFiles = newMethodSet.systemCodeFiles
            }
            return item
          })
          setMethodologySets(updatedMethods)
          setCurrentMethodologySet({
            ...currentMethodologySet,
            description: newMethodSet.description,
            selectedSystems: newMethodSet.selectedSystems,
            starccmConfig: newMethodSet.starccmConfig,
            systemCodeFiles: newMethodSet.systemCodeFiles
          } as MethodologySet)
        }

        // Clear the temp run ID after successful update
        setAeroTempRunId(null)
        localStorage.removeItem('aero_temp_run_id')
        toast.success('Methodology set updated.')
        appEventInstance.reloadMethodology()
      } catch (err) {
        console.error(err)
        toast.error('Error updating methodology set.')
      }
    }
  }

  const handleCreateNewMethodologySet = () => {
    if (mode === 'new' || mode === 'copy') {
      toast.error('Cannot create a new methodology set while creating a new one.')
      return
    }

    const newIndexes = new Map(sectionIndexes)
    const newSectionVisibilities = new Map(sectionVisibilities)
    const newSelectedSystems: SelectedSystem[] = []
    sections.forEach((section, index) => {
      newIndexes.set(section, index)
      newSectionVisibilities.set(section, false)
      newSelectedSystems.push({} as SelectedSystem)
    })

    setSectionIndexes(newIndexes)
    setSelectedSystems(newSelectedSystems)
    setSectionVisibilities(newSectionVisibilities)

    reset()
    setMode('new')
    setValue('name', '')
    setValue('description', '')
    setValue('status', METHODOLOGY_STATUS_DRAFT)
    // Clear any previous upload state
    setSelectedSimTemplateFiles([])
    setSelectedCodeFiles([])
    setAeroTempRunId(null)
    setJobRunParams([])
    localStorage.removeItem('aero_temp_run_id')
    setCurrentMethodologySet({
      systemCodeFiles: [],
      starccmConfig: {
        starccmParameters: {
          paramList: [] as BasicNameValue[]
        } as StarCCMParameterList,
        miscellaneousParameters: {
          paramList: [] as BasicNameValue[]
        } as MiscellaneousParameterList,
        macroSteps: starCCMMacroSteps.map(
          step => new BasicNameValue({ name: step.name, value: step.value })
        ),
        customSteps: [] as string[],
        stepOrder: starCCMMacroSteps.map(step => step.value)
      } as StarCCMConfig
    } as MethodologySet)

    sections.forEach(section => {
      const secIndex = newIndexes.get(section)
      setValue(`selectedSystems.${secIndex}.name`, newSelectedSystems[secIndex].name)
      setValue(`selectedSystems.${secIndex}.notes`, newSelectedSystems[secIndex].notes)
      setValue(
        `selectedSystems.${secIndex}.vendorVersion`,
        newSelectedSystems[secIndex].vendorVersion
      )
      setValue(`selectedSystems.${secIndex}.branchName`, newSelectedSystems[secIndex].branchName)
      setValue(`selectedSystems.${secIndex}.commitId`, newSelectedSystems[secIndex].commitId)
      setValue(
        `selectedSystems.${secIndex}.entryScriptId`,
        newSelectedSystems[secIndex].entryScriptId
      )
      setValue(`selectedSystems.${secIndex}.isHardwareOverride`, false)
    })

    // Clear file uploads for new methodology
    setSelectedSimTemplateFiles([])
    setSelectedCodeFiles([])
  }

  const handleItemSelected = methodologySet => {
    if (!methodologySet) {
      return
    }
    const newIndexes = new Map(sectionIndexes)
    const newSectionVisibilities = new Map(sectionVisibilities)
    const newSelectedSystems: SelectedSystem[] = []
    sections.forEach((section, index) => {
      newIndexes.set(section, index)
      newSectionVisibilities.set(section, false)
      newSelectedSystems.push({} as SelectedSystem)
    })

    methodologySet.selectedSystems.forEach(item => {
      if (!sections.includes(item.name)) {
        return
      }
      const sectionKey = item.name
      const selectionIndex = newIndexes.get(sectionKey)
      newSectionVisibilities.set(sectionKey, !!item.vendorVersion)

      // Migrate legacy hardware overrides: if the per-system fields are empty but the
      // methodology-level fields exist (old data), populate per-system from top-level.
      const hasPerSystemHardware = item.isHardwareOverride || item.hardwareOverrides
      const useLegacyHardware =
        !hasPerSystemHardware &&
        methodologySet.isHardwareOverride &&
        methodologySet.hardwareOverrides &&
        (sectionKey === 'starccmp' || sectionKey === 'ensight')

      newSelectedSystems[selectionIndex] = {
        name: item.name,
        notes: item.notes,
        vendorVersion: item.vendorVersion,
        branchName: item.branchName,
        commitId: item.commitId,
        entryScriptId: item.entryScriptId,
        isHardwareOverride: useLegacyHardware
          ? methodologySet.isHardwareOverride
          : item.isHardwareOverride || false,
        hardwareOverrides: useLegacyHardware
          ? methodologySet.hardwareOverrides
          : item.hardwareOverrides,
        nodes: item.nodes,
        coresPerNode: item.coresPerNode,
        maxCoresCount: item.maxCoresCount
      } as SelectedSystem
    })

    setSectionIndexes(newIndexes)
    setSelectedSystems(newSelectedSystems)
    setSectionVisibilities(newSectionVisibilities)
    setCurrentMethodologySet({
      ...methodologySet,
      systemCodeFiles: methodologySet.systemCodeFiles || [],
      starccmConfig:
        methodologySet.starccmConfig ||
        ({
          starccmParameters: {
            name: '',
            paramList: [] as BasicNameValue[]
          } as StarCCMParameterList,
          miscellaneousParameters: {
            name: '',
            paramList: [] as BasicNameValue[]
          } as MiscellaneousParameterList,
          macroSteps: [] as BasicNameValue[],
          customSteps: [] as string[],
          stepOrder: [] as string[]
        } as StarCCMConfig)
    })

    // Populate file upload states from existing systemCodeFiles
    const existingFiles = methodologySet.systemCodeFiles || []

    // Get unique template files (deduplicate by name)
    const templateFileMap = new Map<string, FileData>()
    existingFiles
      .filter(f => f.fileRef?.fileType === 'template')
      .forEach(f => {
        const fileName = f.fileName || f.fileRef?.file || ''
        if (fileName && !templateFileMap.has(fileName)) {
          templateFileMap.set(fileName, {
            name: fileName,
            path: f.fileRef?.path || f.fileRef?.file || fileName,
            progress: 100,
            uploading: false,
            failed: false
          })
        }
      })
    const templateFiles = Array.from(templateFileMap.values())

    // Get code files from systemCodeFiles
    const codeFilesFromSystem = existingFiles
      .filter(f => f.fileRef?.fileType === 'code')
      .map(f => ({
        name: f.fileName || f.fileRef?.file || '',
        path: f.fileRef?.path || f.fileRef?.file || f.fileName || '',
        progress: 100,
        uploading: false,
        failed: false
      }))

    // Also check customSteps for files that might not be in systemCodeFiles (data inconsistency handling)
    const customSteps = methodologySet.starccmConfig?.customSteps || []
    const codeFileNames = new Set(codeFilesFromSystem.map(f => f.name))
    const missingFilesFromCustomSteps = customSteps
      .filter(stepFile => stepFile.endsWith('.java')) // Only include .java files, not macro steps
      .filter(stepFile => !codeFileNames.has(stepFile))
      .map(stepFile => ({
        name: stepFile,
        path: stepFile,
        progress: 100,
        uploading: false,
        failed: false
      }))

    // Merge both sources
    const codeFiles = [...codeFilesFromSystem, ...missingFilesFromCustomSteps]

    setSelectedSimTemplateFiles(templateFiles)
    setSelectedCodeFiles(codeFiles)
    setJobRunParams(
      Array.from(toStringMap(methodologySet.jobRunParams).entries()).map(([key, value]) => ({
        key,
        value
      }))
    )
    // Clear temp run ID when switching to edit mode (editing existing methodology)
    setAeroTempRunId(null)
    localStorage.removeItem('aero_temp_run_id')

    setMode('edit')

    reset()

    // init form data
    setValue('name', methodologySet.name)
    setValue('description', methodologySet.description)
    setValue('status', methodologySet.status || METHODOLOGY_STATUS_DRAFT)
    sections.forEach(section => {
      const secIndex = newIndexes.get(section)
      setValue(`selectedSystems.${secIndex}.name`, newSelectedSystems[secIndex].name)
      setValue(`selectedSystems.${secIndex}.notes`, newSelectedSystems[secIndex].notes)
      setValue(
        `selectedSystems.${secIndex}.vendorVersion`,
        newSelectedSystems[secIndex].vendorVersion
      )
      setValue(`selectedSystems.${secIndex}.branchName`, newSelectedSystems[secIndex].branchName)
      setValue(`selectedSystems.${secIndex}.commitId`, newSelectedSystems[secIndex].commitId)
      setValue(
        `selectedSystems.${secIndex}.entryScriptId`,
        newSelectedSystems[secIndex].entryScriptId
      )
      setValue(
        `selectedSystems.${secIndex}.isHardwareOverride`,
        newSelectedSystems[secIndex].isHardwareOverride || false
      )
      setValue(`selectedSystems.${secIndex}.nodes`, newSelectedSystems[secIndex].nodes)
      setValue(`selectedSystems.${secIndex}.coresPerNode`, newSelectedSystems[secIndex].coresPerNode)
      setValue(
        `selectedSystems.${secIndex}.maxCoresCount`,
        newSelectedSystems[secIndex].maxCoresCount
      )
      if (newSelectedSystems[secIndex].hardwareOverrides) {
        setValue(
          `selectedSystems.${secIndex}.hardwareOverrides.coreType`,
          newSelectedSystems[secIndex].hardwareOverrides.coreType
        )
        setValue(
          `selectedSystems.${secIndex}.hardwareOverrides.slots`,
          newSelectedSystems[secIndex].hardwareOverrides.slots
        )
        setValue(
          `selectedSystems.${secIndex}.hardwareOverrides.coresPerSlot`,
          newSelectedSystems[secIndex].hardwareOverrides.coresPerSlot
        )
        setValue(
          `selectedSystems.${secIndex}.hardwareOverrides.walltime`,
          newSelectedSystems[secIndex].hardwareOverrides.walltime
        )
      }
    })
  }

  const handleCopyMethodologySet = e => {
    if (mode === 'new' || mode === 'copy') {
      toast.error('Cannot duplicate while creating a new methodology set.')
      return
    }
    if (!currentMethodologySet._id) {
      toast.error('Please select a methodology set to duplicate.')
      return
    }

    setMode('copy')
    setValue('description', currentMethodologySet.description)
    setValue('status', METHODOLOGY_STATUS_DRAFT)
  }

  // Only admin, developer, and methodology-specialist roles may view/change the status.
  const canEditMethodologyStatus = () => {
    return (
      !!currentUser &&
      (currentUser.role?.roleId === 'admin' ||
        currentUser.role?.roleId === 'developer' ||
        currentUser.role?.roleId === 'methodology-specialist')
    )
  }

  const isDevModeActive = () => {
    if (
      currentUser &&
      (currentUser.role?.roleId === 'admin' ||
        currentUser.role?.roleId === 'developer' ||
        currentUser.role?.roleId === 'methodology-specialist')
    ) {
      return true
    }
    return false
  }

  // Check if we're duplicating with files for loading state
  const isDuplicatingFiles =
    creating &&
    mode === 'copy' &&
    (currentMethodologySet?.systemCodeFiles?.length > 0 || currentMethodologySet?.starccm?.template)

  return (
    <>
      {/* Loading overlay for file duplication */}
      {isDuplicatingFiles && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center shadow-2xl">
            <LoadingIcon className="animate-spin h-16 w-16 fill-gm-blue mb-4" />
            <p className="text-xl font-semibold text-gray-800">Duplicating Methodology Files...</p>
            <p className="text-sm text-gray-600 mt-2">This may take a moment</p>
          </div>
        </div>
      )}

      <div className="basis-0 grow flex flex-col items-stretch mt-6 min-w-full space-y-4">
        <div className="flex items-center justify-between pb-4">
          <h1 className="page-title">Methodology Sets</h1>
          {env === 'dev' && (
            <Button
              type="button"
              onClick={handleCreateNewMethodologySet}
              severity="secondary"
              className="icon-button hidden"
              disabled={mode === 'new' || mode === 'copy'}
              tooltip="Create New Methodology Set"
              tooltipOptions={{ position: 'bottom' }}
              data-pr-at="center+45 bottom">
              <PlusIcon className="fill-white" />
            </Button>
          )}
        </div>
        <form
          onSubmit={handleSubmit(onSubmit, onValidationError)}
          className="w-full grid grid-1 gap-x-2 gap-y-4 mt-8 pb-10">
          {currentMethodologySet && env === 'dev' && scrollY > 200 && (
            <div className="flex items-center space-x-2 sticky top-0 z-10 py-2 bg-white opacity-90  ">
              <div className="flex-grow basis-0 flex items-center space-x-2 text-lg">
                {mode === 'new' ? 'New Methodology' : currentMethodologySet.name}
              </div>
              <div className="basis-[250px] flex flex-col justify-end items-end">
                <SaveButton
                  loading={creating || updating}
                  disabled={hasUploadingFiles}
                  label="Save Methodology"
                />
                {hasUploadingFiles && (
                  <small className="text-amber-600 dark:text-amber-400 text-xs mt-1">
                    Waiting for file uploads to complete...
                  </small>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <div className="flex-grow basis-0 flex items-center space-x-2">
              <Dropdown
                filter
                loading={loadingMethodologySets}
                filterBy="name"
                filterMatchMode="contains"
                showFilterClear
                value={currentMethodologySet}
                emptyMessage="No MethodologySets Found"
                pt={{
                  ...DropdownStyle,
                  root: {
                    className: DropdownStyle.root.className + ' basis-0 flex-grow'
                  }
                }}
                optionValue="_id"
                optionLabel="name"
                useOptionAsValue
                options={methodologySets}
                valueTemplate={option => {
                  let value
                  if (mode === 'new') {
                    value = 'New Methodology Set'
                  } else if (option) {
                    value =
                      option.name +
                      (mode === 'copy' ? ' - Copy' : '') +
                      (option.description ? ` (${option.description})` : '')
                  } else {
                    value =
                      (currentMethodologySet && currentMethodologySet.name) ||
                      'Select Methodology Set'
                  }
                  return <div>{value}</div>
                }}
                itemTemplate={option => {
                  return (
                    <div>
                      {option && option._id ? `${option.name}` : ''}
                      {option.description ? ` (${option.description})` : ''}
                    </div>
                  )
                }}
                onChange={e => handleItemSelected(e.value)}
                placeholder="Select Methodology Set"
              />
            </div>
            <div className="flex items-center basis-[250px] justify-between space-x-2">
              {env === 'dev' && (
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {currentMethodologySet?._id && mode !== 'new' && (
                    <Button
                      type="button"
                      onClick={handleCopyMethodologySet}
                      className="icon-button"
                      tooltip="Duplicate Methodology Set"
                      tooltipOptions={{ position: 'bottom' }}
                      data-pr-at="center+5 bottom">
                      <CopyIcon className="fill-white w-4 h-4" />
                    </Button>
                  )}
                  {currentMethodologySet && (
                    <div className="flex flex-col items-end gap-1">
                      <SaveButton loading={creating || updating} disabled={hasUploadingFiles} />
                      {hasUploadingFiles && (
                        <small className="text-amber-600 dark:text-amber-400 text-xs">
                          Waiting for file uploads to complete...
                        </small>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isDevModeActive() && (
                <div className="flex items-center space-x-2 flex-shrink-0 justify-end grow">
                  <InputSwitch
                    checked={env === 'dev'}
                    onChange={e => {
                      setEnv(e.value ? 'dev' : 'prod')
                    }}
                    pt={{
                      root: {
                        className: '!h-[24px] w-12'
                      },
                      slider: {
                        className: classNames('h-[24px] !w-12', {
                          'dark:!bg-gray-500': env === 'prod',
                          'dark:!bg-neutral-10': env === 'dev'
                        })
                      }
                    }}
                  />
                  <label className="ml-1">Dev Mode</label>
                </div>
              )}
            </div>
          </div>

          <div className="col-span-1 mt-4 flex items-start gap-4">
            <div className="field-box flex-1">
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className="mb-2">
                      Description
                    </label>
                    <InputText
                      id={field.name}
                      disabled={creating || updating || !currentMethodologySet || env === 'prod'}
                      {...field}
                      className={classNames({ error: fieldState.error })}
                    />
                  </>
                )}
              />
            </div>

            {canEditMethodologyStatus() && (
              <div className="field-box basis-64 flex-shrink-0">
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <label htmlFor={field.name} className="mb-2">
                        Status
                      </label>
                      <Dropdown
                        id={field.name}
                        value={field.value}
                        pt={DropdownStyle}
                        appendTo="self"
                        disabled={creating || updating || !currentMethodologySet || env === 'prod'}
                        options={MethodologyStatusList}
                        onChange={e => field.onChange(e.value)}
                        className={classNames({ error: fieldState.error })}
                        placeholder="Select Status"
                      />
                    </>
                  )}
                />
              </div>
            )}
          </div>

          {sections.map(sectionKey => {
            return (
              <>
                <div className="field-box col-span-1 mt-4" key={sectionKey + '-checkbox'}>
                  <div className="flex-grow flex items-center">
                    <Checkbox
                      inputId={sectionKey}
                      disabled={creating || updating || !currentMethodologySet || env === 'prod'}
                      pt={CheckboxStyle}
                      checked={sectionVisibilities.get(sectionKey)}
                      onChange={e => {
                        sectionVisibilities.set(sectionKey, e.checked)
                        setSectionVisibilities(new Map(sectionVisibilities))
                      }}></Checkbox>
                    <label
                      htmlFor={sectionKey}
                      className={classNames('ml-2 font-normal cursor-pointer', {
                        'text-gray-400':
                          creating || updating || !currentMethodologySet || env === 'prod'
                      })}>
                      {'Show ' + sectionNames.get(sectionKey)}
                    </label>
                  </div>
                </div>

                {currentMethodologySet && sectionVisibilities.get(sectionKey) && (
                  <MethodSetSection
                    key={sectionKey + '-form'}
                    sectionKey={sectionKey}
                    sectionIndex={sectionIndexes.get(sectionKey)}
                    deploySets={sectionDeploySets.get(sectionKey)}
                    sectionName={sectionNames.get(sectionKey)}
                    selectedSystem={selectedSystems[sectionIndexes.get(sectionKey)]}
                    control={control}
                    setValue={setValue}
                    disabled={creating || updating || env === 'prod'}
                    mode={mode}
                    formErrors={errors}
                  />
                )}

                {sectionVisibilities.get(sectionKey) &&
                  currentMethodologySet &&
                  currentMethodologySet.starccmConfig &&
                  sectionKey === 'starccmp' && (
                    <>
                      {/* StarCCM File Upload Section */}
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <Upload
                          label="Sim Template Files"
                          field={{ name: 'simTemplateFiles' }}
                          fieldState={{}}
                          setValue={() => {}}
                          selectedFiles={selectedSimTemplateFiles}
                          setSelectedFiles={setSelectedSimTemplateFiles}
                          setTempId={setAeroTempRunId}
                          getFormErrorMessage={() => null}
                          uploadType={UploadTypes.STARCCM_SIM_FILE}
                          blobPrefix={`${currentMethodologySet.name || 'new'}/starccm/template`}
                          isNew={mode === 'new'}
                          deleteConfirmationTarget="this Methodology"
                          deletePersistedFilesFromStorage={mode !== 'copy'}
                          disabled={creating || updating || env === 'prod'}
                        />
                        <Upload
                          label="Code/Macro Files"
                          field={{ name: 'codeFiles' }}
                          fieldState={{}}
                          setValue={() => {}}
                          selectedFiles={selectedCodeFiles}
                          setSelectedFiles={setSelectedCodeFiles}
                          setTempId={setAeroTempRunId}
                          getFormErrorMessage={() => null}
                          uploadType={UploadTypes.STARCCM_MACRO_FILE}
                          blobPrefix={`${currentMethodologySet.name || 'new'}/starccm/code`}
                          isNew={mode === 'new'}
                          deleteConfirmationTarget="this Methodology"
                          deletePersistedFilesFromStorage={mode !== 'copy'}
                          disabled={creating || updating || env === 'prod'}
                        />
                      </div>

                      <StarCCMRunConfigForm
                        key={currentMethodologySet._id}
                        customMacros={customMacros}
                        selectedSteps={selectedMacroSteps}
                        selectedStarCCMPreset={selectedStarCCMPreset}
                        selectedMiscPreset={selectedMiscPreset}
                        raceTeamId={currentMethodologySet.raceTeamId}
                        readOnly={creating || updating || env === 'prod'}
                        restrictPresets={creating || updating || env === 'prod'}
                        allowAllDefaultSteps
                        onConfigChange={(config: StarCCMConfig) => {
                          refStarCCMConfig.current = config
                        }}
                      />

                      <HardwareSettingsSection
                        key={sectionKey + '-hardware-' + (currentMethodologySet?._id || 'new')}
                        sectionKey={sectionKey}
                        sectionIndex={sectionIndexes.get(sectionKey)}
                        sectionName={sectionNames.get(sectionKey)}
                        hardwareOverrides={
                          selectedSystems[sectionIndexes.get(sectionKey)]?.hardwareOverrides
                        }
                        isHardwareOverride={
                          selectedSystems[sectionIndexes.get(sectionKey)]?.isHardwareOverride ||
                          false
                        }
                        nodes={selectedSystems[sectionIndexes.get(sectionKey)]?.nodes || 1}
                        coresPerNode={selectedSystems[sectionIndexes.get(sectionKey)]?.coresPerNode || 0}
                        maxCoresCount={selectedSystems[sectionIndexes.get(sectionKey)]?.maxCoresCount || 0}
                        control={control}
                        setValue={setValue}
                        disabled={creating || updating || env === 'prod'}
                        mode={mode}
                        formErrors={errors}
                      />
                      {selectedSystems[sectionIndexes.get(sectionKey)]?.nodes}-
                      {selectedSystems[sectionIndexes.get(sectionKey)]?.coresPerNode}-
                      {selectedSystems[sectionIndexes.get(sectionKey)]?.maxCoresCount}
                    </>
                  )}

                {sectionVisibilities.get(sectionKey) &&
                  currentMethodologySet &&
                  sectionKey === 'ensight' && (
                    <HardwareSettingsSection
                      key={sectionKey + '-hardware-' + (currentMethodologySet?._id || 'new')}
                      sectionKey={sectionKey}
                      sectionIndex={sectionIndexes.get(sectionKey)}
                      sectionName={sectionNames.get(sectionKey)}
                      hardwareOverrides={
                        selectedSystems[sectionIndexes.get(sectionKey)]?.hardwareOverrides
                      }
                      isHardwareOverride={
                        selectedSystems[sectionIndexes.get(sectionKey)]?.isHardwareOverride || false
                      }
                      nodes={selectedSystems[sectionIndexes.get(sectionKey)]?.nodes || 1}
                      coresPerNode={selectedSystems[sectionIndexes.get(sectionKey)]?.coresPerNode || 0}
                      maxCoresCount={selectedSystems[sectionIndexes.get(sectionKey)]?.maxCoresCount || 0}
                      control={control}
                      setValue={setValue}
                      disabled={creating || updating || env === 'prod'}
                      mode={mode}
                      formErrors={errors}
                    />
                  )}
              </>
            )
          })}

          {currentMethodologySet && (
            <JobRunParamsSection
              params={jobRunParams}
              onChange={setJobRunParams}
              disabled={creating || updating || env === 'prod'}
            />
          )}
        </form>
      </div>
    </>
  )
}

export default MethodologySets
